/**
 * MitgliederPage — Administration › Mitglieder (Story UI-U6 AC3; Fix 5).
 *
 * Searchable table of tenant members (initials-avatar + mono user_id, E-Mail,
 * Rolle(n) as soft badges, Status, Mitglied seit) over GET /admin/members. Fix 5
 * adds the in-app broker-employee INVITE: a "Mitarbeiter einladen" dialog (email,
 * Anzeigename, optional Rolle), gated by `role.manage`, plus pending invitations
 * rendered in the same table with a 3-state badge (Aktiv / Einladung versendet /
 * Einladung ausstehend) and an "Einladung erneut senden" action on invited rows.
 *
 * Per-row "Rollen verwalten" (role.manage) assigns/unassigns Epic-12 roles via the
 * EXISTING 12.6 endpoints.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import type { AdminInvitationDto, AdminMemberDto, AdminMemberState } from '@brokly/shared';
import { DataTable } from '../../../components/data/DataTable';
import { Badge } from '../../../components/ui/Badge';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useAdminMembers, useAdminRoles, useAdminUserRoles } from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import { Button, Dialog, LoadingState, ErrorState, EmptyState, TextInput } from '../components';

const meta = ADMIN_PAGE_META.mitglieder;

/** Normalized table row — a real member OR a pending invitation. */
interface MemberRow {
  /** Stable table key. */
  rowId: string;
  kind: 'member' | 'invite';
  display_name: string | null;
  email: string;
  role_labels: string[];
  created_at: string;
  state: AdminMemberState;
  member?: AdminMemberDto;
  invite?: AdminInvitationDto;
}

function initials(name: string | null, email: string): string {
  const src = name ?? email;
  return src
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join('');
}

function fmtDate(d: string): string {
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('de-CH');
}

const STATE_BADGE: Record<AdminMemberState, { label: string; variant: 'neutral' | 'info' | 'warning' }> =
  {
    active: { label: 'Aktiv', variant: 'neutral' },
    invited: { label: 'Einladung versendet', variant: 'info' },
    pending: { label: 'Einladung ausstehend', variant: 'warning' },
  };

// ── Mitarbeiter einladen dialog ─────────────────────────────────────────────
function InviteDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const allRoles = useAdminRoles();
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [roleId, setRoleId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      apiClient.admin.inviteMember({
        email: email.trim(),
        displayName: displayName.trim(),
        ...(roleId ? { roleId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminMembers() });
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const canSubmit = email.trim().length > 0 && displayName.trim().length > 0 && !invite.isPending;

  return (
    <Dialog
      open
      onClose={onClose}
      title="Mitarbeiter einladen"
      description="Die Einladung wird per E-Mail versendet; die Aktivierung erfolgt über den Einladungslink."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={invite.isPending}>
            Abbrechen
          </Button>
          <Button disabled={!canSubmit} onClick={() => invite.mutate()}>
            {invite.isPending ? 'Wird gesendet …' : 'Einladung senden'}
          </Button>
        </>
      }
    >
      {error != null && <div className="mb-3 text-sm text-status-danger">{error}</div>}
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            E-Mail
          </label>
          <TextInput
            type="email"
            autoFocus
            placeholder="name@firma.ch"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Anzeigename
          </label>
          <TextInput
            placeholder="Vor- und Nachname"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Rolle (optional)
          </label>
          <select
            aria-label="Rolle wählen"
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Standardrolle</option>
            {(allRoles.data?.roles ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Dialog>
  );
}

// ── Rollen verwalten dialog ─────────────────────────────────────────────────
function RollenDialog({ member, onClose }: { member: AdminMemberDto; onClose: () => void }) {
  const qc = useQueryClient();
  const userRoles = useAdminUserRoles(member.user_id);
  const allRoles = useAdminRoles();
  const [roleToAdd, setRoleToAdd] = useState('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.adminUserRoles(member.user_id) });
    void qc.invalidateQueries({ queryKey: qk.adminMembers() });
  };

  const assign = useMutation({
    mutationFn: (roleId: string) => apiClient.admin.assignRole(member.user_id, roleId),
    onSuccess: () => {
      setRoleToAdd('');
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });
  const unassign = useMutation({
    mutationFn: (roleId: string) => apiClient.admin.unassignRole(member.user_id, roleId),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const held = userRoles.data?.roles ?? [];
  const heldIds = new Set(held.map((r) => r.id));
  const assignable = (allRoles.data?.roles ?? []).filter((r) => !heldIds.has(r.id));

  return (
    <Dialog
      open
      onClose={onClose}
      title="Rollen verwalten"
      description={member.display_name ?? member.email}
      footer={<Button variant="outline" onClick={onClose}>Schliessen</Button>}
    >
      {error != null && <div className="mb-3 text-sm text-status-danger">{error}</div>}
      <div className="space-y-4">
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Zugewiesene Rollen
          </div>
          {held.length === 0 ? (
            <div className="text-sm text-muted-foreground">Keine Rollen zugewiesen.</div>
          ) : (
            <ul className="space-y-1.5">
              {held.map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between rounded-md border border-border px-3 py-2"
                >
                  <span className="flex items-center gap-2 text-sm">
                    {r.name}
                    {r.is_system && <Badge variant="neutral">System</Badge>}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={unassign.isPending}
                    onClick={() => unassign.mutate(r.id)}
                  >
                    Entfernen
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Rolle hinzufügen
          </div>
          <div className="flex items-center gap-2">
            <select
              aria-label="Rolle wählen"
              value={roleToAdd}
              onChange={(e) => setRoleToAdd(e.target.value)}
              className="h-9 flex-1 rounded-md border border-border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="">Rolle wählen …</option>
              {assignable.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <Button
              disabled={!roleToAdd || assign.isPending}
              onClick={() => roleToAdd && assign.mutate(roleToAdd)}
            >
              Hinzufügen
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}

// ── Resend button (per invited row) ─────────────────────────────────────────
function ResendButton({ invitationId }: { invitationId: string }) {
  const qc = useQueryClient();
  const resend = useMutation({
    mutationFn: () => apiClient.admin.resendInvitation(invitationId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.adminMembers() }),
  });
  return (
    <Button
      variant="ghost"
      size="sm"
      disabled={resend.isPending || resend.isSuccess}
      onClick={() => resend.mutate()}
    >
      {resend.isSuccess ? 'Gesendet' : resend.isPending ? 'Sendet …' : 'Einladung erneut senden'}
    </Button>
  );
}

export function MitgliederPage() {
  const { can } = useEffectivePermissions();
  const canManage = can('role.manage');
  const { data, isLoading, isError } = useAdminMembers();
  const allRoles = useAdminRoles();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdminMemberDto | null>(null);
  const [inviting, setInviting] = useState(false);

  // role_id → name map so pending invitations can show their role label.
  const roleNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of allRoles.data?.roles ?? []) m.set(r.id, r.name);
    return m;
  }, [allRoles.data]);

  const rows = useMemo<MemberRow[]>(() => {
    const memberRows: MemberRow[] = (data?.members ?? []).map((m) => ({
      rowId: `member:${m.user_id}`,
      kind: 'member',
      display_name: m.display_name,
      email: m.email,
      role_labels: m.role_names,
      created_at: m.created_at,
      state: 'active',
      member: m,
    }));
    const inviteRows: MemberRow[] = (data?.invitations ?? []).map((inv) => ({
      rowId: `invite:${inv.id}`,
      kind: 'invite',
      display_name: inv.display_name,
      email: inv.email,
      role_labels: inv.role_id != null ? [roleNameById.get(inv.role_id) ?? inv.role_id] : [],
      created_at: inv.created_at,
      state: inv.state,
      invite: inv,
    }));
    const all = [...memberRows, ...inviteRows];

    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (r) =>
        (r.display_name ?? '').toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.role_labels.some((n) => n.toLowerCase().includes(q)),
    );
  }, [data, roleNameById, search]);

  const columns = useMemo<ColumnDef<MemberRow>[]>(() => {
    const cols: ColumnDef<MemberRow>[] = [
      {
        id: 'member',
        header: 'Mitglied',
        accessorFn: (r) => r.display_name ?? r.email,
        cell: ({ row }) => {
          const r = row.original;
          return (
            <div className="flex items-center gap-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-semibold text-primary">
                {initials(r.display_name, r.email)}
              </span>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{r.display_name ?? '-'}</div>
                <div className="truncate font-mono text-[11px] tabular-nums text-muted-foreground">
                  {r.member ? r.member.user_id : '-'}
                </div>
              </div>
            </div>
          );
        },
      },
      { accessorKey: 'email', header: 'E-Mail' },
      {
        id: 'roles',
        header: 'Rolle(n)',
        cell: ({ row }) => {
          const names = row.original.role_labels;
          if (names.length === 0) return <span className="text-muted-foreground">-</span>;
          return (
            <div className="flex flex-wrap gap-1">
              {names.map((n) => (
                <Badge key={n} variant="neutral">
                  {n}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const s = STATE_BADGE[row.original.state];
          return <Badge variant={s.variant}>{s.label}</Badge>;
        },
      },
      {
        id: 'created',
        header: 'Mitglied seit',
        accessorFn: (r) => r.created_at,
        cell: ({ row }) => (
          <span className="tabular-nums text-muted-foreground">
            {fmtDate(row.original.created_at)}
          </span>
        ),
      },
    ];
    if (canManage) {
      cols.push({
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const r = row.original;
          if (r.kind === 'member') {
            return (
              <Button variant="ghost" size="sm" onClick={() => setEditing(r.member!)}>
                Rollen verwalten
              </Button>
            );
          }
          // Invited rows can be re-sent; a not-yet-sent (pending) row shows nothing.
          return r.state === 'invited' ? <ResendButton invitationId={r.invite!.id} /> : null;
        },
      });
    }
    return cols;
  }, [canManage]);

  return (
    <div>
      <AdminPageHeader
        title={meta.title}
        description={meta.description}
        actions={
          canManage ? (
            <Button onClick={() => setInviting(true)}>Mitarbeiter einladen</Button>
          ) : undefined
        }
      />
      <p className="mb-4 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
        Laden Sie neue Mitarbeitende per E-Mail ein. Sie aktivieren ihren Zugang selbst über den
        Einladungslink. Firmen können auch über die Brokly-Plattformverwaltung angelegt werden.
      </p>
      <div className="mb-3 max-w-sm">
        <TextInput
          type="search"
          placeholder="Suchen (Name, E-Mail, Rolle) …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState />
      ) : rows.length === 0 ? (
        <EmptyState label="Keine Mitglieder gefunden." />
      ) : (
        <DataTable columns={columns} data={rows} />
      )}
      {editing != null && <RollenDialog member={editing} onClose={() => setEditing(null)} />}
      {inviting && <InviteDialog onClose={() => setInviting(false)} />}
    </div>
  );
}
